import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import SoulumoHeader from "../../components/SoulumoHeader";
import { API, auth, INTENSITY, SERIF, createTogether } from "../../components/soulumoPractice/practiceApi";
import { TogetherShare } from "../../components/together/TogetherShare";
import { InstallationPicker } from "../../components/together/InstallationPicker";
import { TogetherRhythm } from "../../components/together/TogetherRhythm";

const today = () => new Date().toLocaleDateString("en-CA");
const opt = (on) => `flex items-baseline justify-between rounded-2xl border px-4 py-3 text-left transition-colors ${on ? "border-[#d7bb7a] bg-[#d7bb7a]/10" : "border-[#2b3d5e] hover:border-[#d7bb7a]/50"}`;
const Label = ({ children }) => <p className="mt-9 text-[0.62rem] uppercase tracking-[0.28em] text-[#d7bb7a]">{children}</p>;

// 21 Days Together set-up: one installation, a start date and a rhythm. Then a link to share.
export default function TogetherSetupPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const jid = params.get("j");
  const group = params.get("from") === "coach";
  const [j, setJ] = useState(null);
  const [start, setStart] = useState(today());
  const [intensity, setIntensity] = useState("standard");
  const [night, setNight] = useState(false);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  useEffect(() => { if (jid) axios.get(`${API}/api/audio-journeys/${jid}`, auth()).then((r) => setJ(r.data)).catch(() => setJ(false)); else setJ(null); }, [jid]);
  const create = async (replace = false) => {
    setBusy(true);
    try { setMade(await createTogether({ journey_id: jid, start_date: start, intensity, night, replace })); window.dispatchEvent(new Event("soulumo:practice")); }
    catch (e) {
      if (e?.response?.data?.detail === "has_plan") {
        if (window.confirm("You already have a 21-day plan running. Start this shared one instead? Your current installation stays in your library.")) return create(true);
      } else toast.error("Couldn't create the invitation. Please try again.");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#0b1a33] text-[#f3ecd9]" data-testid="together-setup-page">
      <SoulumoHeader />
      <main className="mx-auto max-w-lg px-6 pb-24 pt-6">
        <button type="button" onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#b9ad8f] hover:text-[#f3ecd9]" data-testid="together-back-btn"><ArrowLeft size={14} /> Back</button>
        <p className="mt-10 text-[0.62rem] uppercase tracking-[0.32em] text-[#d7bb7a]"><Users size={12} className="mr-1 inline -mt-0.5" /> {j?.name || "Your installation"}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-light text-[#f3ecd9]" style={{ fontFamily: SERIF }}>21 Days Together</h1>
        <p className="mt-4 text-xl italic text-[#e9e2d0]" style={{ fontFamily: SERIF }}>Choose one installation. Share one intention. Follow the same rhythm for 21 days.</p>
        <p className="mt-4 text-sm leading-relaxed text-[#cfc6ae]">{group
          ? "Finish your workshop or session with one shared intention. Choose an installation, pick the start date and rhythm, then show the QR code on screen or send the link. Everyone follows it in their own Soulumo planner."
          : "Invite the people you care about, your team, your clients or your community to experience the same installation alongside you. Everyone follows the experience through their own Soulumo planner, together but without pressure."}</p>
        {!jid && !made && <InstallationPicker onPick={(id) => setParams({ ...Object.fromEntries(params), j: id })} />}
        {j === false && <p className="mt-8 text-sm text-[#e0857a]" data-testid="together-not-found">We couldn't find that installation in your library.</p>}
        {made ? <TogetherShare made={made} title={j?.name} /> : j && (
          <>
            <Label>Start date</Label>
            <input type="date" value={start} min={today()} onChange={(e) => setStart(e.target.value)} data-testid="together-start-date"
              className="mt-3 w-full rounded-2xl border border-[#2b3d5e] bg-transparent px-4 py-3 text-[#f3ecd9] [color-scheme:dark]" />
            <Label>Rhythm</Label>
            <div className="mt-3 grid gap-2">
              {INTENSITY.map(([id, label, note]) => (
                <button key={id} type="button" onClick={() => setIntensity(id)} className={opt(intensity === id)} data-testid={`together-intensity-${id}`}>
                  <span className="text-lg" style={{ fontFamily: SERIF }}>{label}</span><span className="text-xs text-[#b9ad8f]">{note}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-[#8f8a78]">More listens simply means more moments in the day with these words.</p>
            <label className="mt-6 flex items-center gap-3 text-sm" data-testid="together-night-toggle">
              <input type="checkbox" checked={night} onChange={(e) => setNight(e.target.checked)} className="h-4 w-4 accent-[#d7bb7a]" /> Include the Night Installation
            </label>
            <p className="mt-1 text-xs text-[#8f8a78]">One 3-hour Night Version you create, shared with everyone who joins as a member.</p>
            <div className="mt-6 rounded-2xl border border-[#2b3d5e] p-4" data-testid="together-rhythm-preview">
              <p className="text-[0.6rem] uppercase tracking-[0.26em] text-[#d7bb7a]">Everyone's day will look like this</p>
              <TogetherRhythm intensity={intensity} night={night} />
            </div>
            <button type="button" onClick={() => create()} disabled={busy || !start} data-testid="together-create-btn"
              className="mt-10 w-full rounded-full bg-[#d7bb7a] py-3.5 text-xs uppercase tracking-[0.3em] text-[#0b1a33] transition-opacity hover:opacity-90 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Create Invitation"}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
